import { db, type Category, type Product } from "./db";
import { categoriesSeed, productsSeed, productCategoriesSeed } from "./seed-data";

export type { Category, Product };

const isServer = typeof window === "undefined";

export function fallbackImage(categoryHandle?: string) {
  if (categoryHandle === "teaware" || categoryHandle === "accessories")
    return "/assets/images/teaware-placeholder.jpg";
  if (categoryHandle === "sample-kits")
    return "/assets/images/sample-kits/sample-kit-universal-placeholder.svg";
  if (categoryHandle === "gift-selection")
    return "/assets/images/gift-selection/gift-selection-universal-placeholder.svg";
  return "/assets/images/tea-placeholder.jpg";
}

export async function fetchCategories(): Promise<Category[]> {
  if (isServer) {
    return [...categoriesSeed].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
    ) as unknown as Category[];
  }
  return await db.categories.orderBy("sort_order").toArray();
}

export async function fetchCategory(handle: string): Promise<Category | null> {
  if (isServer) {
    return (categoriesSeed.find((c) => c.handle === handle) as unknown as Category) || null;
  }
  const cat = await db.categories.where("handle").equals(handle).first();
  return cat ?? null;
}

export async function fetchProductsByCategory(categoryId: string): Promise<Product[]> {
  if (isServer) {
    const productIds = productCategoriesSeed
      .filter((pc) => pc.category_id === categoryId)
      .map((pc) => pc.product_id);
    const products = productsSeed.filter((p) => productIds.includes(p.id) && p.active);
    return products.sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
    ) as unknown as Product[];
  }

  const productCategories = await db.product_categories
    .where("category_id")
    .equals(categoryId)
    .toArray();

  const productIds = productCategories.map((pc) => pc.product_id);

  if (productIds.length === 0) return [];

  const products = await db.products
    .where("id")
    .anyOf(productIds)
    .filter((p) => p.active)
    .toArray();

  return products.sort((a, b) => a.sort_order - b.sort_order);
}

export async function fetchProductByHandle(handle: string) {
  if (isServer) {
    const p = productsSeed.find((p) => p.handle === handle && p.active);
    if (!p) return null;
    const cat = categoriesSeed.find((c) => c.id === p.category_id);
    return { ...p, categories: cat ? { handle: cat.handle, name: cat.name } : null } as any;
  }

  const product = await db.products
    .where("handle")
    .equals(handle)
    .filter((p) => p.active)
    .first();

  if (!product) return null;

  const category = product.category_id
    ? await db.categories.where("id").equals(product.category_id).first()
    : null;

  return {
    ...product,
    categories: category ? { handle: category.handle, name: category.name } : null,
  };
}

export async function fetchFeaturedProducts(limit = 6) {
  if (isServer) {
    const activeProducts = productsSeed
      .filter((p) => p.active)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return activeProducts.slice(0, limit).map((p) => {
      const cat = categoriesSeed.find((c) => c.id === p.category_id);
      return { ...p, categories: cat ? { handle: cat.handle, name: cat.name } : null };
    }) as any;
  }

  const products = await db.products.filter((p) => p.active).toArray();

  products.sort((a, b) => a.sort_order - b.sort_order);
  const limitedProducts = products.slice(0, limit);

  const result = [];
  for (const product of limitedProducts) {
    const category = product.category_id
      ? await db.categories.where("id").equals(product.category_id).first()
      : null;

    result.push({
      ...product,
      categories: category ? { handle: category.handle, name: category.name } : null,
    });
  }

  return result as Array<Product & { categories: { handle: string; name: string } | null }>;
}

export async function fetchAllProducts() {
  if (isServer) {
    const activeProducts = productsSeed
      .filter((p) => p.active)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return activeProducts.map((p) => {
      const cat = categoriesSeed.find((c) => c.id === p.category_id);
      return { ...p, categories: cat ? { handle: cat.handle, name: cat.name } : null };
    }) as any;
  }

  const products = await db.products.filter((p) => p.active).toArray();
  products.sort((a, b) => a.sort_order - b.sort_order);

  const result = [];
  for (const product of products) {
    const category = product.category_id
      ? await db.categories.where("id").equals(product.category_id).first()
      : null;
    result.push({
      ...product,
      categories: category ? { handle: category.handle, name: category.name } : null,
    });
  }
  return result as Array<Product & { categories: { handle: string; name: string } | null }>;
}
